import React, { useState } from "react";
import { Card, Button, Upload, Space, Typography, Steps, message, Table, Tag, Modal, Input, InputNumber, Select, Switch, Tooltip } from "antd";
import Alert from "../components/GxAlert";
import { UploadOutlined, CheckCircleOutlined, TeamOutlined, EyeOutlined } from "@ant-design/icons";
import { previewEmployeesImport, applyEmployeesImport } from "../api/client";
import { byStr } from "../utils/sorters";
import { toJalali } from "../utils/dateUtils";
import { faNum } from "../utils/faNumberFormat";
import { moneyFormatter, moneyParser } from "../utils/numberInput";

const { Title, Text } = Typography;

const fa = (v) => (v == null ? "—" : faNum(Number(v)));

const ACTION_TAG = {
  create: { l: "پرسنل جدید", c: "green" },
  update: { l: "به‌روزرسانی", c: "gold" },
  unchanged: { l: "بدون تغییر", c: "default" },
  conflict: { l: "هم‌نام — رد می‌شود", c: "red" },
};

const PAY_MODEL_LABEL = { piece: "کارمزدی", fixed: "حقوق ثابت", daily: "حقوق ثابت", none: "بدون حقوق ثابت" };

const MATCH_LABEL = {
  national_id: "کد ملی", employee_code: "کد پرسنلی", full_name: "نام", manual: "انتخابِ دستی",
};

//: بخش‌های شناخته‌شده — رانندگان بخشِ مستقلِ خودشان را دارند
const DEPARTMENTS = ["خط تولید", "انبار برش", "انبار کالا", "رانندگان", "مدیریت", "بسته‌بندی"];

const FIELD_LABEL = {
  job_title: "سمت", pay_model: "مدل حقوق", has_insurance: "بیمه",
  monthly_salary: "حقوق ثابت", base_supplement: "پایه تکمیلی",
  national_id: "کد ملی", nationality: "ملیت", identity_doc: "مدارک",
  phone: "تلفن", address: "آدرس", hire_date: "تاریخ استخدام",
  bank_name: "بانک", iban: "شبا", card_number: "کارت",
  account_holder: "صاحب حساب", device_user_id: "شناسه دستگاه",
  department_id: "بخش", daily_rate: "نرخ روزانه", pay_components: "مؤلفه‌های حقوقی",
};

export default function EmployeeImportPage() {
  const [personnelFile, setPersonnelFile] = useState(null);
  const [salaryFile, setSalaryFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [rowErrors, setRowErrors] = useState([]);
  //: اصلاحاتِ دستیِ کاربر، کلید = شمارهٔ سطرِ اکسل. خودِ فایل هرگز تغییر نمی‌کند.
  const [overrides, setOverrides] = useState({});

  // بخش‌های موجود در فایل + بخش‌های استانداردِ برنامه، بدونِ تکرار
  const departmentOptions = React.useMemo(() => {
    const fromFile = (preview?.rows || []).map((r) => r.department).filter(Boolean);
    return [...new Set([...DEPARTMENTS, ...fromFile])].map((d) => ({ value: d, label: d }));
  }, [preview]);

  const overrideList = () =>
    Object.entries(overrides).map(([excel_row, v]) => ({ excel_row: Number(excel_row), ...v }));

  /** یک فیلد را اصلاح می‌کند و پیش‌نمایش را دوباره می‌گیرد تا اثرش دیده شود. */
  const editRow = async (excel_row, patch) => {
    const next = { ...overrides, [excel_row]: { ...(overrides[excel_row] || {}), ...patch } };
    setOverrides(next);
    const list = Object.entries(next).map(([r, v]) => ({ excel_row: Number(r), ...v }));
    setLoading(true);
    try {
      const res = await previewEmployeesImport(personnelFile, salaryFile, list);
      setPreview(res.data);
      setRowErrors([]);
    } catch (e) {
      const d = e?.response?.data?.detail;
      message.error(typeof d === "string" ? d : d?.message || "اصلاح اعمال نشد");
    }
    setLoading(false);
  };

  const handlePreview = async () => {
    if (!personnelFile) {
      message.warning("فایل «لیست پرسنل» را انتخاب کنید");
      return;
    }
    setLoading(true);
    try {
      const res = await previewEmployeesImport(personnelFile, salaryFile);
      setPreview(res.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      message.error(typeof d === "string" ? d : d?.message || "خطا در خواندن فایل");
    }
    setLoading(false);
  };

  const confirmImport = async () => {
    setApplying(true);
    try {
      const res = await applyEmployeesImport(personnelFile, salaryFile, overrideList());
      setResult(res.data);
      message.success(res.data.message);
      setPreview(null);
      setOverrides({});
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (typeof d === "string") {
        message.error(d);
      } else {
        // خطای واقعیِ هر ردیف باید دیده شود؛ جملهٔ کلی به‌تنهایی عیب‌یابی را ناممکن می‌کرد
        setRowErrors(d?.errors || d?.conflicts || []);
        message.error(d?.message || "خطا در ایمپورت — هیچ تغییری ذخیره نشد");
      }
    }
    setApplying(false);
  };

  const columns = [
    { title: "نام", dataIndex: "full_name", width: 200, sorter: byStr("full_name"),
      render: (v, r) => (
        <Space size={4}>
          <span>{v}</span>
          {r.duplicate_name && <Tag color="orange">هم‌نام در فایل</Tag>}
          {r.is_multi_role && <Tag color="geekblue">چندنقشی</Tag>}
        </Space>
      ) },
    { title: "سمت", dataIndex: "job_title", width: 150,
      render: (v, r) => (
        <Input size="small" defaultValue={v} key={`jt-${r.excel_row}-${v}`}
          onBlur={(e) => e.target.value !== (v || "")
            && editRow(r.excel_row, { job_title: e.target.value })} />
      ) },
    { title: "بخش", dataIndex: "department", width: 140,
      render: (v, r) => (
        <Select size="small" style={{ width: "100%" }} value={v} showSearch
          options={departmentOptions}
          onChange={(nv) => editRow(r.excel_row, { department: nv })} />
      ) },
    { title: "مدل حقوق", dataIndex: "pay_model", width: 120,
      render: (v, r) => (
        <Select size="small" style={{ width: "100%" }} value={v}
          options={Object.entries(PAY_MODEL_LABEL).map(([value, label]) => ({ value, label }))}
          onChange={(nv) => editRow(r.excel_row, { pay_model: nv })} />
      ) },
    { title: "کد ملی", dataIndex: "national_id", width: 130,
      render: (v, r) => (
        <Input size="small" defaultValue={v} key={`nid-${r.excel_row}-${v}`}
          onBlur={(e) => e.target.value !== (v || "")
            && editRow(r.excel_row, { national_id: e.target.value })} />
      ) },
    { title: "پایه (ریال)", dataIndex: "amount_target", width: 190, align: "left",
      render: (t, r) => {
        const field = t === "base_supplement" ? "base_supplement" : "monthly_salary";
        const v = r.monthly_salary ?? r.base_supplement;
        return (
          <Space size={4} direction="vertical" style={{ width: "100%" }}>
            <InputNumber size="small" style={{ width: "100%" }} defaultValue={v}
              key={`amt-${r.excel_row}-${v}`} min={0}
              /* پارسرِ مشترک: اول ارقامِ فارسی/عربی → لاتین، بعد فیلتر.
                 با `[^\d]`ِ خام، هر رقمِ فارسی بی‌صدا حذف می‌شد. */
              formatter={moneyFormatter}
              parser={moneyParser}
              onBlur={(e) => {
                const nv = Number(moneyParser(e.target.value)) || null;
                if (nv !== v) editRow(r.excel_row, { [field]: nv });
              }} />
            <Text type="secondary" style={{ fontSize: 11 }}>
              {t === "base_supplement" ? "(حداقل ضمانت)" : "(حقوق ثابت)"}
            </Text>
          </Space>
        );
      } },
    { title: "شغل دوم", dataIndex: "secondary_amount", width: 190, align: "left",
      render: (v, r) => {
        if (!v) return <Text type="secondary">—</Text>;
        const extra = (r.pay_components || []).filter((c) => !c.is_primary);
        return (
          <span>
            <b style={{ color: "#7B61FF" }}>{fa(v)}</b>{" "}
            <Text type="secondary" style={{ fontSize: 11 }}>
              ({extra.map((c) => c.job_title || "—").join("، ")})
            </Text>
          </span>
        );
      } },
    { title: "بیمه", dataIndex: "has_insurance", width: 90,
      render: (v, r) => (
        <Switch size="small" checked={!!v} checkedChildren="دارد" unCheckedChildren="ندارد"
          onChange={(nv) => editRow(r.excel_row, { has_insurance: nv })} />
      ) },
    { title: "استخدام", dataIndex: "hire_date", width: 100,
      render: (v) => (v ? toJalali(v) : "—") },
    { title: "وضعیت", dataIndex: "action", width: 230,
      render: (v, r) => (
        <Space size={4} direction="vertical" style={{ width: "100%" }}>
          <Space size={4}>
            <Tag color={ACTION_TAG[v]?.c}>{ACTION_TAG[v]?.l || v}</Tag>
            {r.match && <Text type="secondary" style={{ fontSize: 11 }}>{MATCH_LABEL[r.match] || r.match}</Text>}
          </Space>
          {/* ابهامِ هم‌نام در **دیتابیس** است؛ تنها راهِ رفعش انتخابِ صریحِ همان نفر است */}
          {v === "conflict" && (r.candidates || []).length > 0 && (
            <Select size="small" style={{ width: "100%" }} placeholder="کدام پرسنل است؟"
              value={overrides[r.excel_row]?.match_employee_id}
              onChange={(id) => editRow(r.excel_row, { match_employee_id: id })}
              options={r.candidates.map((cd) => ({
                value: cd.id,
                label: `${cd.employee_code} — ${cd.job_title || "بدون سمت"} (${cd.department || "—"})`,
              }))} />
          )}
        </Space>
      ) },
    { title: "حذف", width: 70, align: "center",
      render: (_, r) => (
        <Tooltip title="این ردیف وارد نشود">
          <Button size="small" danger type="text"
            onClick={() => editRow(r.excel_row, { skip: true })}>حذف</Button>
        </Tooltip>
      ) },
    { title: "تغییرات", dataIndex: "changes", ellipsis: true,
      render: (ch) => {
        const keys = Object.keys(ch || {});
        if (!keys.length) return <Text type="secondary">—</Text>;
        return <Text style={{ fontSize: 11 }}>
          {keys.map((k) => FIELD_LABEL[k] || k).join("، ")}
        </Text>;
      } },
  ];

  const uploadRow = (label, hint, file, setFile) => (
    <div>
      <Text strong>{label}</Text>
      {hint && <Text type="secondary" style={{ fontSize: 12 }}> — {hint}</Text>}
      <div style={{ marginTop: 8 }}>
        <Upload
          beforeUpload={(f) => { setFile(f); setPreview(null); setOverrides({}); setRowErrors([]); return false; }}
          showUploadList={false}
          accept=".xlsx,.xls"
          maxCount={1}
        >
          <Button icon={<UploadOutlined />}>
            {file ? `✓ ${file.name}` : `انتخاب ${label}`}
          </Button>
        </Upload>
      </div>
    </div>
  );

  return (
    <div>
      <Title level={5} style={{ marginBottom: 16 }}>
        ایمپورت لیست پرسنل <Text type="secondary" style={{ fontSize: 12 }}>(همهٔ مبالغ به ریال)</Text>
      </Title>

      <Steps
        direction="horizontal"
        size="small"
        style={{ marginBottom: 24 }}
        items={[
          { title: "لیست پرسنل", description: "نام، سمت، ملیت، کد ملی، بانکی",
            status: personnelFile ? "finish" : "process" },
          { title: "پایه حقوق ثابت", description: "اختیاری — نام، نوع اشتغال، پایه حقوق",
            status: salaryFile ? "finish" : (personnelFile ? "process" : "wait") },
          { title: "پیش‌نمایش و تأیید", status: result ? "finish" : "wait" },
        ]}
      />

      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          {uploadRow("لیست پرسنل", "اجباری", personnelFile, setPersonnelFile)}
          {uploadRow("لیست پایه حقوق ثابت", "اختیاری", salaryFile, setSalaryFile)}
          <Space wrap>
            <Button
              type="primary" icon={<EyeOutlined />} size="large"
              loading={loading} onClick={handlePreview} disabled={!personnelFile}
            >
              پیش‌نمایش ایمپورت
            </Button>
            {(personnelFile || salaryFile) && (
              <Button onClick={() => { setPersonnelFile(null); setSalaryFile(null); setPreview(null); }}>
                پاک‌کردن فایل‌ها
              </Button>
            )}
          </Space>
        </Space>
      </Card>

      {result && (
        <Alert
          type="success" icon={<CheckCircleOutlined />} showIcon
          message={result.message}
          description={
            <span style={{ fontSize: 12 }}>
              {result.duplicate_names?.length > 0 &&
                <>نام‌های تکراریِ فایل (نیاز به بررسی): <b>{result.duplicate_names.join("، ")}</b><br /></>}
              {result.salary_only?.length > 0 &&
                <>در لیست حقوق ولی بدون پرسنلِ منطبق: <b>{result.salary_only.map((s) => s.full_name).join("، ")}</b></>}
            </span>
          }
        />
      )}

      <Modal
        title="پیش‌نمایش ایمپورت پرسنل — تا تأیید نکنید چیزی ذخیره نمی‌شود"
        open={!!preview}
        onOk={confirmImport}
        onCancel={() => setPreview(null)}
        okText="تأیید و ثبت" cancelText="انصراف"
        confirmLoading={applying}
        width={1200}
        okButtonProps={{ icon: <TeamOutlined />, disabled: !preview?.rows?.length }}
      >
        {preview && (
          <>
            <Alert
              type="info" showIcon style={{ marginBottom: 12 }}
              message={preview.message}
              description={
                <span style={{ fontSize: 12 }}>
                  ردیف لیست پرسنل: <b>{fa(preview.personnel_rows)}</b>
                  {" | "}نفراتِ لیست پایه حقوق: <b>{fa(preview.salary_rows)}</b>
                  {" | "}منطبق‌شده: <b>{fa(preview.salary_matched)}</b>
                  {preview.new_departments?.length > 0 &&
                    <> | بخش‌های جدید: <b>{preview.new_departments.join("، ")}</b></>}
                </span>
              }
            />
            <Space wrap style={{ marginBottom: 12 }}>
              <Tag color="green">جدید: {fa(preview.summary.create)}</Tag>
              <Tag color="gold">به‌روزرسانی: {fa(preview.summary.update)}</Tag>
              <Tag>بدون تغییر: {fa(preview.summary.unchanged)}</Tag>
              {preview.summary.conflict > 0 && <Tag color="red">هم‌نامِ مبهم: {fa(preview.summary.conflict)}</Tag>}
            </Space>
            {preview.salary_only?.length > 0 && (
              <Alert
                type="warning" showIcon style={{ marginBottom: 12 }}
                message="این نفرات در لیست پایه حقوق هستند ولی در لیست پرسنل پیدا نشدند — پایه حقوقشان ثبت نمی‌شود"
                description={<span style={{ fontSize: 12 }}>
                  {preview.salary_only.map((s) => `${s.full_name} (${fa(s.base_amount)} ریال)`).join("  |  ")}
                </span>}
              />
            )}
            {preview.multi_role?.length > 0 && (
              <Alert
                type="info" showIcon style={{ marginBottom: 12 }}
                message={`${fa(preview.multi_role.length)} نفر بیش از یک شغل دارند — برای هر شغل یک مؤلفهٔ حقوقی ساخته می‌شود`}
                description={<span style={{ fontSize: 12 }}>
                  {preview.multi_role.map((m) =>
                    `${m.full_name}: ` + m.components.map((c) => `${c.job_title || "—"} ${fa(c.amount)}`).join(" + ")
                  ).join("  |  ")}
                </span>}
              />
            )}
            {preview.summary.conflict > 0 && (
              <Alert
                type="error" showIcon style={{ marginBottom: 12 }}
                message="ردیف‌های هم‌نامِ مبهم رد می‌شوند — برای این نفرات کد ملی را در فایل یا در صفحهٔ پرسنل تکمیل کنید"
              />
            )}
            {rowErrors.length > 0 && (
              <Alert
                type="error" showIcon closable style={{ marginBottom: 12 }}
                onClose={() => setRowErrors([])}
                message={`${rowErrors.length} ردیف خطا داد — هیچ تغییری ذخیره نشد`}
                description={
                  <div style={{ maxHeight: 220, overflow: "auto" }}>
                    {rowErrors.map((er, i) => (
                      <div key={i} style={{ fontSize: 12, marginBottom: 6, direction: "ltr",
                                            textAlign: "left", wordBreak: "break-word" }}>
                        {String(er)}
                      </div>
                    ))}
                  </div>
                }
              />
            )}
            <Table
              size="small" rowKey={(r) => `${r.excel_row}-${r.full_name}`}
              columns={columns} dataSource={preview.rows}
              pagination={{ pageSize: 12, showSizeChanger: false }}
              scroll={{ x: "max-content" }}
            />
          </>
        )}
      </Modal>
    </div>
  );
}
